import api from './api';
import { getApiErrorMessage } from '../utils/errorHandler';

// Token management utilities
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_DATA_KEY = 'userData';

const setTokens = (accessToken, refreshToken) => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
};

const getTokens = () => ({
    accessToken: localStorage.getItem(TOKEN_KEY),
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY)
});

export { getTokens };

const clearTokens = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_DATA_KEY);
};

export const login = async (credentials) => {
    try {
        const response = await api.post('/api/token/', credentials);

        if (response.data.access) {
            setTokens(response.data.access, response.data.refresh);
            if (response.data.user) {
                localStorage.setItem(USER_DATA_KEY, JSON.stringify(response.data.user));
            }
        }

        return response.data;
    } catch (error) {
        if (error.response) {
            const message = getApiErrorMessage(error, 'Invalid credentials. Please try again.');
            throw new Error(message);
        } else if (error.request) {
            throw new Error('Unable to connect to the server. Please check your internet connection.');
        } else {
            throw new Error('An unexpected error occurred. Please try again later.');
        }
    }
};

export const register = async (userData) => {
    try {
        const response = await api.post('/api/users/users/', {
            email: userData.email,
            password: userData.password,
            first_name: userData.first_name,
            last_name: userData.last_name,
            phone: userData.phone,
        });
        return response.data;
    } catch (error) {
        if (error.response?.data) {
            const errorMessage = Object.entries(error.response.data)
                .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
                .join('\n');
            throw new Error(errorMessage);
        }
        throw new Error('Registration failed. Please try again later.');
    }
};

export const logout = () => {
    clearTokens();
};

export const refreshToken = async () => {
    try {
        const { refreshToken } = getTokens();
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        const response = await api.post('/api/token/refresh/', {
            refresh: refreshToken
        });

        if (response.data.access) {
            setTokens(response.data.access, response.data.refresh);
        }

        return response.data;
    } catch (error) {
        clearTokens();
        if (error.response) {
            throw new Error('Session expired. Please log in again.');
        } else if (error.request) {
            throw new Error('Unable to refresh session. Please check your connection.');
        } else {
            throw new Error('An unexpected error occurred while refreshing session.');
        }
    }
};

export const getCurrentUser = async () => {
    try {
        const response = await api.get('/api/users/users/me/');
        if (response.data) {
            localStorage.setItem(USER_DATA_KEY, JSON.stringify(response.data));
        }
        return response.data;
    } catch (error) {
        if (error.response) {
            throw new Error('Unable to fetch user data.');
        } else if (error.request) {
            throw new Error('Unable to connect to the server.');
        } else {
            throw new Error('An unexpected error occurred.');
        }
    }
};

export const isAuthenticated = () => {
    return !!localStorage.getItem(TOKEN_KEY);
};

export const verifyToken = async (token) => {
    try {
        const response = await api.post('/api/token/verify/', { token });
        return response.data;
    } catch (error) {
        if (error.response) {
            throw new Error('Token verification failed.');
        } else if (error.request) {
            throw new Error('Unable to verify token. Please check your connection.');
        } else {
            throw new Error('An unexpected error occurred during token verification.');
        }
    }
};

// Auto-refresh token utility
export const setupTokenRefresh = () => {
    const { accessToken } = getTokens();
    if (!accessToken) return;

    try {
        // Decode JWT to get expiration time
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        const expirationTime = payload.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const timeUntilExpiry = expirationTime - currentTime;

        // If token expires in less than 5 minutes, refresh it
        if (timeUntilExpiry < 5 * 60 * 1000) {
            refreshToken().catch(() => {});
        } else {
            const refreshTime = timeUntilExpiry - (5 * 60 * 1000);
            setTimeout(() => {
                refreshToken().catch(() => {});
            }, refreshTime);
        }
    } catch (error) {
        // Non-fatal: token refresh timer could not be set up
    }
};

// Get stored user data
export const getStoredUserData = () => {
    try {
        const userData = localStorage.getItem(USER_DATA_KEY);
        return userData ? JSON.parse(userData) : null;
    } catch {
        return null;
    }
}; 